import { Flex, VStack } from '@chakra-ui/layout'
import { findAll } from 'highlight-words-core'
import { t } from 'i18next'
import { concat, equals } from 'ramda'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import iconSharedVoicemail from '~/assets/icons-light-theme/icon-shared-voicemail.svg'
import {
  ItemWrapper,
  UnreadWrapper,
} from '~/components/compositional/ListViewStyles'
import { FilterMenu, Text } from '~/components/core'
import { FilterOption } from '~/components/core/FilterMenu'
import { FontStyle } from '~/components/core/Text'
import EmptySearch from '~/components/EmptySearch'
import { EmptyState } from '~/components/EmptyState'
import { ImageIcon } from '~/components/ImageIcon'
import ListItem from '~/components/ListItem'
import {
  DropdownSelect,
  SelectItem,
} from '~/components/shared/Dropdown/DropdownSelect'
import { MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import {
  ScrollingList,
  ScrollToIndexParams,
} from '~/components/shared/ScrollingList'
import { SearchInput } from '~/components/shared/SearchInput'
import { dark, light } from '~/constants/theme'
import urls from '~/constants/urls'
import { ExtensionTypeValue } from '~/queries/extensionQueries'
import { UnreadVoicemailStatusFilter } from '~/types/ApiRequestTypes'
import { SearchValue } from '~/types/Shared'
import { hasValue } from '~/utils/common'
import { convertVoicemalLength, formatRelativeDay } from '~/utils/formatter'
import { openInNewTab } from '~/utils/functions'

import { Extension, Voicemail } from '@mangovoice/types'

import {
  SharedVMWrapper,
  SubtitleWrapper,
  TitleWrapper,
} from './Voicemail.styles'

interface VoicemailListViewProps {
  voicemailList?: Voicemail[]
  listDataLoading?: boolean
  listDataFetching?: boolean
  selectedVoicemailId?: number
  selectedVoicemail?: Voicemail
  selectedExtension?: number | string
  voicemailExtensions?: Extension[]
  unreadFilter?: UnreadVoicemailStatusFilter
  searchValue?: SearchValue
  onVoicemailSelected?: (selectedVoicemail: Voicemail) => void
  onExtensionSelected?: (selectedExtension: string | number) => void
  onUnreadFilterChanged?: (newFilterValue: UnreadVoicemailStatusFilter) => void
  onSearchFocusedChanged?: (focused: boolean) => void
  onSearchChanged?: (newSearchValue: SearchValue) => void
  onNextPageRequested?: () => void
}

const listViewFilterOptions: () => FilterOption[] = () => [
  {
    label: t('voicemail.filter.all'),
    value: UnreadVoicemailStatusFilter.ALL,
    default: true,
  },
  {
    label: t('voicemail.filter.unread'),
    value: UnreadVoicemailStatusFilter.TRUE,
  },
]

export const VoicemailListView = ({
  voicemailList,
  listDataLoading,
  selectedExtension,
  voicemailExtensions,
  unreadFilter,
  searchValue = SearchValue.empty,
  onVoicemailSelected,
  onExtensionSelected,
  onSearchChanged,
  onUnreadFilterChanged,
  onNextPageRequested,
}: VoicemailListViewProps) => {
  const gtag = window.gtag
  const { voicemailId } = useParams()
  const { t } = useTranslation()

  const selectedVoicemailIndex = voicemailList?.findIndex(
    (voicemail) => voicemail.id === Number(voicemailId),
  )
  const scrollToIndex: ScrollToIndexParams = {
    index: selectedVoicemailIndex ?? -1,
    options: { align: 'start', behavior: 'auto' },
  }

  const handleClickItem = (item: Voicemail) => (e: React.MouseEvent) => {
    e.preventDefault()
    onVoicemailSelected?.(item)
    gtag('event', 'voicemail_selected', { id: item.id })
  }

  const handleOpenSupport = (e: MouseEvent) => {
    e.preventDefault()
    openInNewTab(urls.SUPPORT)
  }

  const handleExtensionSelected = (id: number | string, index: number) => {
    if (index === 0) {
      onExtensionSelected?.('')
      return
    }
    onExtensionSelected?.(id)
  }

  const extOptions =
    voicemailExtensions
      ?.filter((e) => e.voicemail_status)
      ?.map((extension) => {
        const { id, name, voicemail_status } = extension
        const unreadCount =
          voicemail_status && voicemail_status.unread_count > 999
            ? '999+'
            : (voicemail_status?.unread_count ?? 0)
        const unreadIndicator =
          unreadCount !== 0 ? (
            <UnreadWrapper>{unreadCount}</UnreadWrapper>
          ) : undefined
        const label = (
          <MenuListItem primaryText={name} rightContent={unreadIndicator} />
        )
        return { value: id, selectedLabel: name, content: label }
      }) ?? []

  const options = concat(
    [{ value: 0, content: t('voicemail.filter.allInboxes') }],
    extOptions,
  ) as SelectItem<string | number>[]

  const listContent = useMemo(() => {
    if (voicemailList && voicemailList.length === 0 && !listDataLoading) {
      if (hasValue(searchValue.label)) {
        return <EmptySearch search={searchValue.value} />
      } else {
        return (
          //temp until rewritten - stretches the EmptyState box
          <Flex
            direction="column"
            height="100%"
            justifyContent="flex-start"
            alignItems="flex-start"
            bg="gray.100"
          >
            <EmptyState
              title={t('voicemail.emptyState.title')}
              subtitle={t('voicemail.emptyState.subtitle1')}
              subtitle2={t('voicemail.emptyState.subtitle2')}
              buttonText={t('voicemail.emptyState.buttonText')}
              onClick={handleOpenSupport}
            />
          </Flex>
        )
      }
    }

    return (
      <ScrollingList
        display="flex"
        flex="1"
        width="100%"
        height="70%"
        backgroundColor="gray.200"
        px=".25rem"
        py=".75rem"
        scrollToIndex={scrollToIndex}
        showLoaderFooter={listDataLoading}
        scrollbarOptions={{ showOnHoverOnly: true }}
        itemData={voicemailList ?? []}
        onEndReached={() => onNextPageRequested?.()}
        onBuildItemContent={(_, voicemail: Voicemail) => {
          const {
            id,
            caller_id_name,
            created_at,
            length_in_seconds,
            read_at,
            extension_id,
          } = voicemail

          const isSharedVoicemail =
            (voicemailExtensions?.find((ext) => ext.id === extension_id)
              ?.type as ExtensionTypeValue) === 'ExtensionSharedVoicemailBox'
          const voicemailName = caller_id_name
          const unRead = read_at === null
          const selected = equals(Number(voicemailId), id)
          const chunks = findAll({
            autoEscape: true,
            searchWords: [searchValue.value],
            textToHighlight: voicemailName,
          })
          const voicemailBoxName =
            voicemailExtensions?.find((vm) => vm.id === extension_id)?.name ??
            ''
          const titleElement = hasValue(searchValue.value) ? (
            <TitleWrapper>
              {chunks.map((chunk, index) => {
                const { end, highlight, start } = chunk
                let text = voicemailName.substr(start, end - start)

                //is the next character a space?  Handle it...
                if (voicemailName[end] === ' ') {
                  text += '\u00A0'
                }

                if (voicemailName[start - 1] === ' ') {
                  text = '\u00A0' + text
                }

                return (
                  <div
                    key={`key-${index}`}
                    style={{
                      backgroundColor: highlight
                        ? dark.yellow200
                        : 'transparent',
                    }}
                  >
                    <Text
                      fontStyle={FontStyle.Normal}
                      text={text}
                      color={light.dark900}
                      weight={unRead ? 'bold' : 'normal'}
                    />
                  </div>
                )
              })}
            </TitleWrapper>
          ) : (
            <Text
              fontStyle={FontStyle.Normal}
              text={voicemailName}
              color={light.dark900}
              weight={unRead ? 'bold' : 'normal'}
            />
          )
          return (
            <ListItem
              key={`voicemail-${id}`}
              activated={selected}
              title={titleElement}
              subTitle={
                <SubtitleWrapper>
                  {isSharedVoicemail && (
                    <SharedVMWrapper>
                      <ImageIcon
                        width={20}
                        height={10}
                        icon={iconSharedVoicemail}
                      />
                    </SharedVMWrapper>
                  )}
                  <Text
                    fontStyle={FontStyle.Tiny}
                    text={voicemailBoxName}
                    color={light.dark900}
                  />
                </SubtitleWrapper>
              }
              date={
                <Text
                  fontStyle={FontStyle.Small}
                  text={formatRelativeDay(created_at)}
                  color={light.dark700}
                />
              }
              time={
                <Text
                  fontStyle={FontStyle.Tiny}
                  text={convertVoicemalLength(length_in_seconds)}
                  color={light.dark700}
                />
              }
              onClick={handleClickItem(voicemail)}
            />
          )
        }}
      />
    )
  }, [voicemailList, listDataLoading, searchValue, voicemailId, unreadFilter])

  return (
    <VStack display="flex" height="100%" alignSelf="stretch" spacing="2px">
      <Flex
        width="100%"
        padding="1rem"
        justifyContent="center"
        alignItems="center"
        gap="0.75rem"
        alignSelf="stretch"
        backgroundColor="gray.200"
      >
        <SearchInput
          placeholder={t('voicemail.searchPlaceholder')}
          height="40px"
          onChange={onSearchChanged}
        />
      </Flex>
      {options.length > 2 && (
        <Flex
          width="100%"
          padding="1rem"
          justifyContent="center"
          alignItems="center"
          gap="0.75rem"
          alignSelf="stretch"
          backgroundColor="gray.200"
        >
          <DropdownSelect
            items={options}
            onSelect={handleExtensionSelected}
            selectedValue={!selectedExtension ? 0 : selectedExtension}
          />
        </Flex>
      )}

      <ItemWrapper>
        <FilterMenu
          selectedFilter={unreadFilter ?? UnreadVoicemailStatusFilter.ALL}
          options={listViewFilterOptions()}
          onChange={(val) =>
            onUnreadFilterChanged?.(val as UnreadVoicemailStatusFilter)
          }
        />
      </ItemWrapper>
      {listContent}
    </VStack>
  )
}
